import Vue from 'vue'
import _ from 'lodash'
import { store } from '_/vuex/store'
import { api } from '_/services/api'
import { pError } from '_/helpers/logger'
import { mapGetters } from 'vuex'
import i18n from '_/i18n'

const Reference = Vue.component(
  'Reference',
  require('_/components/FieldTypes/References/Reference/Reference.vue').default
)
const ReferenceQuickSearch = Vue.component(
  'ReferenceQuickSearch',
  require('_/components/FieldTypes/References/ReferenceQuickSearch/ReferenceQuickSearch.vue')
    .default
)
const ReferenceSelector = Vue.component(
  'ReferenceSelector',
  require('_/components/FieldTypes/References/ReferenceSelector/ReferenceSelector.vue')
    .default
)

const ReferencesSelector = Vue.component(
  'ReferencesSelector',
  require('_/components/FieldTypes/References/ReferencesSelector/ReferencesSelector.vue')
    .default
)

export default {
  /*
   * Define props
   *
   * @type  {Object}
   */
  props: {
    disabled: {
      type: Boolean,
    },
    id: {
      required: true,
      type: [Number, String],
    },
    multiple: {
      type: Boolean,
    },
    name: {
      required: true,
      type: String,
    },
    placeholder: {
      type: String,
    },
    refreshFields: {
      type: Number,
    },
    relatedTableName: {
      type: [Boolean, String],
    },
    relatedTableNames: {
      type: [Array],
    },
    showLinkExistingEntries: {
      type: [Boolean],
    },
    showCreateNewEntries: {
      type: [Boolean],
    },
    required: {
      type: Boolean,
    },
    value: {
      default: null,
      type: [Array, Number, Object, String],
    },
    focus: {
      default: false,
      type: Boolean,
    },
  },

  /**
   * Set the data property
   *
   * @return  {object}
   */
  data() {
    return {
      /**
       * Toggles the visibility of clear all selected references confirmation buttons
       *
       * @type  {bool}
       */
      confirmClearAllSelectedReferences: false,

      /**
       * Edited reference Id
       *
       * @type  {int}
       */
      editedReferenceId: null,

      /**
       * Edited reference module table name
       *
       * @type  {string}
       */
      editedReferenceModuleTableName: '',

      /**
       * Is reference selector visible
       *
       * @type  {boolean}
       */
      isReferenceSelectorVisible: false,

      /**
       * Toggles refereneces panel visibility
       *
       * @type  {bool}
       */
      referencesPanelVisible: false,

      /**
       * Selected references
       *
       * @type  {array}
       */
      selectedReferences: [],

      /**
       * jQuery Sortable instance reference
       *
       * @type  {object}
       */
      sortable: null,
    }
  },

  /**
   * Set the computed properties
   *
   * @type  {object}
   */
  computed: {
    ...mapGetters({
      photonModule: 'photonModule/photonModule',
    }),

    /**
     * Is clear references button disabled
     *
     * @return  {bool}
     */
    clearReferencesButtonDisabled() {
      return this.disabled || !this.selectedReferences.length > 0
    },

    /**
     * Should the reorder info be shown
     *
     * @return  {bool}
     */
    shouldShowReorderInfo() {
      return (
        !_.isEmpty(this.selectedReferences) &&
        this.selectedReferences.length > 1
      )
    },
  },

  /**
   * Set the methods
   *
   * @type  {object}
   */
  methods: {
    /**
     * A reference module getter
     *
     * @return  {object}
     */
    fetchReferenceModule(relatedTableName) {
      return _.find(this.photonModule.modules, ['table_name', relatedTableName])
    },

    /**
     * A reference module name getter
     *
     * @return  {string}
     */
    fetchReferenceModuleName(relatedTableName) {
      const module = this.fetchReferenceModule(relatedTableName)
      return module.name
    },

    fetchIcon(moduleTableName) {
      const module = _.find(this.photonModule.modules, [
        'table_name',
        moduleTableName.system.module.table_name,
      ])

      return module.icon
    },

    /**
     * Displays the published status
     *
     * @param   {object}  entry
     * @return  {string}
     */
    displayPublishedStatus(entry) {
      const publishedAt = _.get(entry, 'system.published_at', null)
      if (publishedAt) {
        return moment(moment().format('YYYY-MM-DD HH:mm:ss')).isSameOrAfter(
          publishedAt
        )
          ? i18n.t('admin.published')
          : i18n.t('admin.scheduled')
      }
      return i18n.t('admin.unpublished')
    },

    /**
     * Returns the published status boolean
     *
     * @param   {object}  entry
     * @return  {boolean}
     */
    isPublishedStatus(entry) {
      const publishedAt = _.get(entry, 'system.published_at', null)
      if (publishedAt) {
        return moment(moment().format('YYYY-MM-DD HH:mm:ss')).isSameOrAfter(
          publishedAt
        )
      }
      return false
    },

    /**
     * Tries to fetch the first image URL, otherwise returns null
     *
     * @param   {object}  entry
     * @return  {string}
     */
    fetchFirstImage(entry) {
      let url = null

      _.forEach(entry, function(field) {
        if (_.has(field, 'fields.file_url')) {
          url = field.fields.file_url + '?w=120&h=90'
          return false
        }
      })

      return url
    },

    /**
     * Sets the setSelectedReferences property
     */
    setSelectedReferences() {
      this.selectedReferences = this.value ? _.cloneDeep(this.value) : []
    },

    /**
     * Transforms value property
     *
     * @return  {array}
     */
    transformValueProperty() {
      if (_.isEmpty(this.value)) {
        return []
      }

      return this.value
    },

    /**
     * Adds the reference upon selection completion
     */
    closeAndInsertReference(reference) {
      this.referencesPanelVisible = false

      if (!reference) {
        return
      }

      this.selectedReferences.push(reference)

      this.$emit('change', {
        id: this.id,
        name: this.name,
        value: this.selectedReferences,
      })
    },

    /**
     * Adds the references upon selection completion
     */
    closeAndInsertReferences(selectedReferences) {
      this.selectedReferences = this.selectedReferences.concat(
        selectedReferences
      )

      this.closeReferenceSelector()

      this.$emit('change', {
        id: this.id,
        name: this.name,
        value: this.selectedReferences,
      })
    },

    /**
     * Links a reference and hides the reference panel, if opened.
     *
     * @param   {object}  reference
     * @return  {void}
     */
    linkReference(reference = null) {
      this.referencesPanelVisible = false
      this.editedReferenceId = null

      if (!reference) {
        return
      }

      const existingReferenceIndexes = _.keys(
        _.pickBy(this.selectedReferences, selectedReference => {
          return (
            _.get(selectedReference, 'system.id', null) ===
              _.get(reference, 'system.id', null) &&
            _.get(selectedReference, 'system.module.table_name', null) ===
              _.get(reference, 'system.module.table_name', null)
          )
        })
      )

      if (existingReferenceIndexes.length > 0) {
        for (const existingReferenceIndex of existingReferenceIndexes) {
          this.selectedReferences[existingReferenceIndex] = _.cloneDeep(
            reference
          )
        }
      } else {
        this.selectedReferences.push(reference)
      }

      this.$emit('change', {
        id: this.id,
        name: this.name,
        value: this.selectedReferences,
      })
    },

    /**
     * Closes the reference selector modal
     *
     * @return  {void}
     */
    closeReferenceSelector() {
      this.editedReferenceId = null
      this.isReferenceSelectorVisible = false
    },

    /**
     * Opens the reference selector modal
     *
     * @return  {void}
     */
    openReferenceSelector() {
      this.isReferenceSelectorVisible = true
    },

    /**
     * Clears all the references
     *
     * @return  {void}
     */
    clearAllSelectedReferences() {
      this.setConfirmClearAllSelectedReferences(false)
      this.selectedReferences = []

      this.$emit('change', {
        id: this.id,
        name: this.name,
        value: this.selectedReferences,
      })
    },

    /**
     * Removes a selected single reference
     *
     * @param   {integer}  reference
     * @return  {void}
     */
    clearReference(index) {
      this.selectedReferences.splice(index, 1)

      this.$emit('change', {
        id: this.id,
        name: this.name,
        value: this.selectedReferences,
      })
    },

    /**
     * Shows the references panel
     *
     * @param  {object}  editedReference
     * @return  {void}
     */
    openReferencesPanel(editedReference) {
      this.editedReferenceId = editedReference.system.id
      this.editedReferenceModuleTableName =
        editedReference.system.module.table_name
      this.referencesPanelVisible = true
    },

    /**
     * Shows the references panel for new entry creation
     *
     * @param  {string}  relatedTableName
     * @return  {void}
     */
    openReferencesPanelForCreation(relatedTableName) {
      this.editedReferenceModuleTableName = relatedTableName
      this.referencesPanelVisible = true
    },

    /**
     * Toggles the visibility of ClearAllSelectedReferences confirmation buttons
     *
     * @param  {boolean}  value
     * @return  {void}
     */
    setConfirmClearAllSelectedReferences(value) {
      this.confirmClearAllSelectedReferences = value
    },

    /**
     * Sortable fields plugin setup
     *
     * @return  {void}
     */
    initializeSortable() {
      const $sortableFields = $(this.$el).find('.reference-sortable-fields')

      if ($sortableFields.hasClass('ui-sortable')) {
        $sortableFields.sortable('destroy')
      }

      const updateFieldsOrder = this.updateFieldsOrder

      this.sortable = $sortableFields.sortable({
        axis: 'y',
        delay: 150,
        cancel: '.placeholder, .flip-it',
        containment: 'parent',
        cursor: 'grabbing',
        items: '.sortable-item',
        opacity: 0.6,
        revert: false,
        scroll: true,
        scrollSensitivity: 50,
        tolerance: 'pointer',
        stop() {
          const newOrder = $sortableFields.sortable('toArray', {
            attribute: 'data-id',
          })

          updateFieldsOrder(newOrder)
        },
      })
    },

    /**
     * Updates the fields order after drag and drop action
     *
     * @param   {object}  newOrder
     * @return  {void}
     */
    updateFieldsOrder(newOrder) {
      this.$emit('change', {
        id: this.id,
        name: this.name,
        related_table_name: this.relatedTableName,
        value: newOrder.map(dataId => {
          const parsedDataId = dataId.split(':')
          return _.find(this.selectedReferences, reference => {
            return (
              reference.system.id == parsedDataId[1] &&
              reference.system.module.table_name == parsedDataId[0]
            )
          })
        }),
      })
    },
  },

  /**
   * Set the mounted hook
   *
   * @return  {void}
   */
  mounted() {
    this.$nextTick(() => {
      this.setSelectedReferences()

      if (this.multiple) {
        this.initializeSortable()
      }

      if (this.focus) {
        $(this.$el)
          .find('.btn-primary')
          .focus()
      }
    })
  },

  /**
   * Define the watched properties
   *
   * @type  {Object}
   */
  watch: {
    value: {
      deep: true,
      handler(newEntry, oldEntry) {
        this.setSelectedReferences()
        this.$forceUpdate()
      },
    },
  },
}
